import { findBestMove } from "./ai";
import { allLegalMoves, applyMove, isInCheck } from "./moves";
import { moveToSanBase } from "./notation";
import type { GameState, Move, PieceType } from "./types";

export interface AssistantAdvice {
  /** Coup suggere, a mettre en surbrillance sur le plateau ; `null` si la partie est terminee. */
  move: Move | null;
  text: string;
}

/** Profondeur de recherche du conseil : au-dela, le clic bloquerait sensiblement l'interface (recherche synchrone). */
const SEARCH_DEPTH = 3;

const NON_KING_VALUES: Record<PieceType, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };
/** Materiel total (hors rois) en debut de partie, pour situer une position sur l'echelle ouverture -> finale. */
const STARTING_NON_KING_MATERIAL = 2 * (8 * NON_KING_VALUES.p + 2 * NON_KING_VALUES.n + 2 * NON_KING_VALUES.b + 2 * NON_KING_VALUES.r + NON_KING_VALUES.q);

type Phase = "opening" | "middlegame" | "endgame";

function remainingMaterial(state: GameState): number {
  let total = 0;
  for (const piece of state.board) {
    if (piece) total += NON_KING_VALUES[piece.type];
  }
  return total;
}

function gamePhase(state: GameState): Phase {
  const material = remainingMaterial(state);
  if (state.fullmoveNumber <= 10 && material >= STARTING_NON_KING_MATERIAL * 0.85) return "opening";
  if (material <= STARTING_NON_KING_MATERIAL * 0.35) return "endgame";
  return "middlegame";
}

function pieceName(type: PieceType): string {
  switch (type) {
    case "p":
      return "pion";
    case "n":
      return "cavalier";
    case "b":
      return "fou";
    case "r":
      return "tour";
    case "q":
      return "dame";
    case "k":
      return "roi";
  }
}

function randomOf<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)]!;
}

const PHASE_LINES: Record<Phase, readonly string[]> = {
  opening: [
    "On en est encore aux ouvertures, l'ami : dispute le centre et sors tes pieces avant de te lancer.",
    "Debut de partie : mets ton roi a l'abri et developpe sans trainer.",
  ],
  middlegame: [
    "La bagarre du milieu de partie est engagee, garde l'oeil ouvert pour les combinaisons.",
    "Milieu de partie : chaque piece mal defendue est une occasion pour l'un comme pour l'autre.",
  ],
  endgame: [
    "On approche de la finale, l'ami : chaque pion compte, et le roi doit sortir se battre.",
    "Peu de pieces restent sur l'echiquier : precision et activite du roi feront la difference.",
  ],
};

/**
 * Analyse la position au trait et propose un coup (le meilleur trouve par la
 * recherche de `ai.ts`, meme moteur que l'IA "difficile") avec un commentaire
 * du Taverneu adapte a la phase de partie et a la nature du coup. Purement
 * logique : c'est a l'appelant d'afficher le texte et de surligner le coup.
 */
export function getAssistantAdvice(state: GameState): AssistantAdvice {
  const move = findBestMove(state, SEARCH_DEPTH);
  if (!move) {
    return { move: null, text: "Je ne vois plus aucun coup a te souffler, l'ami... la partie touche a sa fin." };
  }

  const legalMoves = allLegalMoves(state);
  const san = moveToSanBase(move, legalMoves);
  const after = applyMove(state, move);
  const opponentInCheck = isInCheck(after, after.turn);
  const opponentHasReply = allLegalMoves(after).length > 0;
  const givesMate = opponentInCheck && !opponentHasReply;
  const selfInCheck = isInCheck(state, state.turn);

  let tacticalLine: string;
  if (selfInCheck) {
    tacticalLine = `Pare l'echec : joue ${san} !`;
  } else if (givesMate) {
    tacticalLine = `Le coup fatal ! Joue ${san} et c'est echec et mat !`;
  } else if (opponentInCheck) {
    tacticalLine = `Joue ${san}, echec au roi adverse !`;
  } else if (move.captured) {
    const majorPiece = move.captured.type === "q" || move.captured.type === "r";
    tacticalLine = majorPiece
      ? `Fonce, l'ami : ${san} empoche ${pieceName(move.captured.type)} adverse !`
      : `Joue ${san}, ca prend ${pieceName(move.captured.type)} adverse.`;
  } else if (move.castle) {
    tacticalLine = `Roque avec ${san} : ton roi sera bien plus a l'abri.`;
  } else if (move.promotion) {
    tacticalLine = `Pousse ce pion, ${san} : une nouvelle piece va naitre !`;
  } else {
    tacticalLine = `Je jouerais ${san}, ${pieceName(move.piece.type)} bien place vaut son pesant d'or.`;
  }

  const phaseLine = randomOf(PHASE_LINES[gamePhase(state)]);
  return { move, text: `${phaseLine} ${tacticalLine}` };
}
